// SRT 字幕解析和生成工具

export interface SRTEntry {
  index: number;
  start: number; // 毫秒
  end: number; // 毫秒
  text: string;
}

// 解析 SRT 格式字幕
export function parseSRT(srtContent: string): SRTEntry[] {
  const entries: SRTEntry[] = [];
  const blocks = srtContent.trim().split(/\n\s*\n/);

  for (const block of blocks) {
    const lines = block.split('\n');
    if (lines.length < 3) continue;

    const index = parseInt(lines[0].trim(), 10);
    const timeLine = lines[1].trim();
    const text = lines.slice(2).join('\n');

    const timeMatch = timeLine.match(
      /(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/
    );

    if (timeMatch) {
      const startTime =
        parseInt(timeMatch[1], 10) * 3600000 +
        parseInt(timeMatch[2], 10) * 60000 +
        parseInt(timeMatch[3], 10) * 1000 +
        parseInt(timeMatch[4], 10);
      const endTime =
        parseInt(timeMatch[5], 10) * 3600000 +
        parseInt(timeMatch[6], 10) * 60000 +
        parseInt(timeMatch[7], 10) * 1000 +
        parseInt(timeMatch[8], 10);

      entries.push({
        index,
        start: startTime,
        end: endTime,
        text: text.trim(),
      });
    }
  }

  return entries;
}

// 生成 SRT 格式字幕
export function generateSRT(entries: SRTEntry[]): string {
  return entries
    .map((entry) => {
      const startTime = formatTime(entry.start);
      const endTime = formatTime(entry.end);
      return `${entry.index}\n${startTime} --> ${endTime}\n${entry.text}`;
    })
    .join('\n\n');
}

// 格式化时间为 SRT 格式
function formatTime(ms: number): string {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const milliseconds = ms % 1000;

  return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)},${padMilliseconds(
    milliseconds
  )}`;
}

function padZero(num: number): string {
  return num.toString().padStart(2, '0');
}

function padMilliseconds(num: number): string {
  return num.toString().padStart(3, '0');
}

// 将秒转换为毫秒
export function secondsToMs(seconds: number): number {
  return Math.round(seconds * 1000);
}

// 将毫秒转换为秒
export function msToSeconds(ms: number): number {
  return ms / 1000;
}

// 格式化文件大小
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

// 格式化时长
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${padZero(minutes)}:${padZero(secs)}`;
  }
  return `${minutes}:${padZero(secs)}`;
}

// 生成任务 ID
export function generateTaskId(): string {
  return `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// 延迟函数
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 重试函数
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      console.error(`Attempt ${attempt} failed:`, lastError.message);

      if (attempt < maxAttempts) {
        await delay(delayMs * attempt);
      }
    }
  }

  throw lastError;
}

// 验证 URL
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// 验证文件类型
export function isValidVideoFile(file: File): boolean {
  const validTypes = [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime',
    'video/x-msvideo',
  ];
  return validTypes.includes(file.type);
}

// 获取文件扩展名
export function getFileExtension(filename: string): string {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}

// 截断文本
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

// 防抖函数
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// 节流函数
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}